"use client";

import { Check } from "@phosphor-icons/react/dist/ssr";
import { Fragment, type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";

import { Container } from "@/src/components/ui/Container";
import Section from "@/src/components/ui/Section";
import { Heading, Text } from "@/src/components/ui/Typography";
import { cn } from "@/src/lib/utils";

// Stagger step between each checklist row's reveal — kept in one place so
// the row animation and its badge/ping delays (set inline per row) can't
// drift out of sync with each other.
const REVEAL_STEP_SECONDS = 0.12;

export interface ArchitectureLayer {
  name: ReactNode;
  description: ReactNode;
}

export interface ArchitectureStackProps {
  title: string;
  intro?: string;
  layers: ArchitectureLayer[];
  whyItMattersTitle: string;
  whyItMatters: string[];
}

export function ArchitectureStack({
  title,
  intro,
  layers,
  whyItMattersTitle,
  whyItMatters,
}: ArchitectureStackProps) {
  const [revealed, setRevealed] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = cardRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setRevealed(true);
        observer.disconnect();
      },
      { threshold: 0.3 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (layers.length === 0) return null;

  return (
    <Section className="bg-neutral-100">
      <Container>
        <Heading as="h2" size="displayLG" className="max-w-175">
          {title}
        </Heading>

        {intro && (
          <Text size="bodyLG" className="mt-4 max-w-175 text-neutral-600">
            {intro}
          </Text>
        )}

        <div className="mt-13 grid gap-x-6 gap-y-8 md:mt-16 md:grid-cols-[1fr_2fr] md:gap-14">
          {layers.map((layer, index) => (
            <Fragment key={index}>
              <Text size="bodyMD" className="text-neutral-700">
                {layer.name}
              </Text>

              <Text size="bodySM" className="text-neutral-600">
                {layer.description}
              </Text>
            </Fragment>
          ))}
        </div>

        <div
          ref={cardRef}
          className="mt-13 rounded-(--radius-lg) border border-neutral-200 bg-white p-6 md:mt-16 md:p-10"
        >
          <Heading as="h3" size="headingMD" className="text-neutral-700">
            {whyItMattersTitle}
          </Heading>

          <div className="mt-6 flex flex-col gap-4">
            {whyItMatters.map((point, index) => {
              const revealDelay = { "--reveal-delay": `${index * REVEAL_STEP_SECONDS}s` } as CSSProperties;

              return (
                <div
                  key={index}
                  style={revealDelay}
                  className={cn("flex items-start gap-3 opacity-0", revealed && "checklist-item-reveal")}
                >
                  <span
                    style={revealDelay}
                    className={cn(
                      "relative mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand opacity-0",
                      revealed && "checklist-badge-pop checklist-ping",
                    )}
                  >
                    <Check size={12} weight="bold" className="relative z-10" />
                  </span>

                  <Text size="bodySM" className="text-neutral-600">
                    {point}
                  </Text>
                </div>
              );
            })}
          </div>
        </div>
      </Container>
    </Section>
  );
}
